// ⑤ 開始画面設計（本家 2026-07-18「ゲーム開始画面の改良」のツール反映）。
// - 背景 1 枚: ④ ワールド設計＋①解析から LLM が英語プロンプトを合成 → OpenRouter 画像生成（文字・人物なし）
// - 立ち絵: プレイアブル（主人公・相方）ごとに ③-1 の subjectEn を流用して見た目を一致させ、透過 PNG 化
// - どちらも自作画像への手動アップロード（差し替え）を常に許可する
// 成果物は projects/<PID>/start-screen/ に保存し、リンク済みゲームへは applyStartScreenToGameDir で反映する。
// 素材が全部そろわない間は artMode=false（ゲームは start-menu--no-art フォールバック）にして壊れた参照を出さない。
import { existsSync, readFileSync, writeFileSync, mkdirSync, unlinkSync } from 'node:fs';
import path from 'node:path';
import { callImage, callLlmJson, llmConfig, resolveImageCallSpec } from './llm-client.mjs';
import { toTransparentPng, clearFootArtifacts } from './image-postprocess.mjs';
import { pipelineConfig, fillTemplate, str, asArray, batchCharacterConcurrency } from './story-shared.mjs';
import {
    projectPaths, requireProjectDir, requireAnalysis, readProjectMeta, readWorldDesign,
    recordStep, addUsage,
} from './story-project.mjs';
import { readJsonIfExists, writeJson } from './json-utils.mjs';
import { auditLog } from './logger.mjs';
import { requireGameDir } from './games.mjs';
import { mapWithConcurrency } from './async-pool.mjs';
import { sniffImageType, IMAGE_EXT_BY_TYPE } from './story-image-gen.mjs';
import { applyStartScreenToGameDir } from './start-screen-package.mjs';

export const START_SCREEN_JOB_KIND = 'story-start-screen';
export const BACKGROUND_SLOT = 'background';
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export function startScreenConfig() {
    const cfg = pipelineConfig().startScreen ?? {};
    return {
        ...cfg,
        defaults: {
            backgroundScale: 1, backgroundPositionY: 50, stageCenterX: 25, panelSide: 'right',
            offsetX: 0, offsetY: 0, scale: 1, maskEnabled: false,
            ...(cfg.defaults ?? {}),
        },
        limits: {
            offsetAbsMax: 400, scaleMin: 0.5, scaleMax: 1.6,
            stageCenterXMin: 10, stageCenterXMax: 45,
            backgroundScaleMin: 1, backgroundScaleMax: 1.6,
            ...(cfg.limits ?? {}),
        },
        layout: { engineFrameCenterX: 25, ...(cfg.layout ?? {}) },
        portraitFootClear: {
            enabled: true, footRegionRatio: 0.24, shadowChromaMax: 26, shadowMinBrightness: 96,
            ...(cfg.portraitFootClear ?? {}),
        },
        artFiles: {
            backgroundBase: 'start-menu-background', characterPrefix: 'start-menu-',
            extensions: ['png', 'jpg', 'jpeg', 'webp'],
            ...(cfg.artFiles ?? {}),
        },
    };
}

const clamp = (v, min, max, fallback) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(max, Math.max(min, n));
};

/** 開始画面の立ち絵対象 = プレイアブル（主人公→相方の順）。 */
export function playableStartScreenCasts(analysis) {
    const rank = { protagonist: 0, partner: 1 };
    return asArray(analysis?.cast)
        .filter((c) => c?.role === 'protagonist' || c?.role === 'partner')
        .sort((a, b) => (rank[a.role] - rank[b.role]) || String(a.id).localeCompare(String(b.id)));
}

function readSettings(p) {
    return readJsonIfExists(p.startScreenJson) ?? { formatVersion: '1.0', background: {}, characters: {} };
}

function writeSettings(p, settings) {
    mkdirSync(p.startScreenDir, { recursive: true });
    writeJson(p.startScreenJson, settings);
    return settings;
}

/** プロジェクト側画像（start-screen/images/<base>.<ext>）を 1 枚だけ保つ（拡張子違いは削除）。 */
function writeSlotImage(p, baseName, bytes, ext) {
    mkdirSync(p.startScreenImagesDir, { recursive: true });
    for (const e of Object.values(IMAGE_EXT_BY_TYPE)) {
        const old = path.join(p.startScreenImagesDir, `${baseName}.${e}`);
        if (e !== ext && existsSync(old)) unlinkSync(old);
    }
    const fileName = `${baseName}.${ext}`;
    writeFileSync(path.join(p.startScreenImagesDir, fileName), bytes);
    return fileName;
}

/** 設定＋実ファイル存在から画面表示・適用に使う状態を組み立てる。 */
export function readStartScreenState(projectId) {
    requireProjectDir(projectId);
    const p = projectPaths(projectId);
    const cfg = startScreenConfig();
    const analysis = readJsonIfExists(p.analysis);
    const settings = readSettings(p);
    const bg = settings.background ?? {};
    const bgFile = str(bg.file);
    const backgroundReady = !!bgFile && existsSync(path.join(p.startScreenImagesDir, bgFile));
    const casts = playableStartScreenCasts(analysis).map((cast) => {
        const c = settings.characters?.[cast.id] ?? {};
        const file = str(c.file);
        const ready = !!file && existsSync(path.join(p.startScreenImagesDir, file));
        return {
            castId: cast.id,
            role: cast.role,
            nameJa: cast.name?.ja ?? cast.id,
            file: ready ? file : null,
            source: c.source ?? null,
            model: c.model ?? null,
            generatedAt: c.generatedAt ?? null,
            promptEn: c.promptEn ?? null,
            offsetX: clamp(c.offsetX, -cfg.limits.offsetAbsMax, cfg.limits.offsetAbsMax, cfg.defaults.offsetX),
            offsetY: clamp(c.offsetY, -cfg.limits.offsetAbsMax, cfg.limits.offsetAbsMax, cfg.defaults.offsetY),
            scale: clamp(c.scale, cfg.limits.scaleMin, cfg.limits.scaleMax, cfg.defaults.scale),
            maskEnabled: c.maskEnabled === true,
            ready,
        };
    });
    const charactersReady = casts.length > 0 && casts.every((c) => c.ready);
    return {
        formatVersion: '1.0',
        // UI の「デフォルトに戻す」用（config/story-pipeline.json startScreen.defaults が正）
        defaults: { ...cfg.defaults },
        panelSide: settings.panelSide === 'left' ? 'left' : cfg.defaults.panelSide,
        background: {
            file: backgroundReady ? bgFile : null,
            source: bg.source ?? null,
            model: bg.model ?? null,
            generatedAt: bg.generatedAt ?? null,
            promptEn: bg.promptEn ?? null,
            promptEdited: bg.promptEdited === true,
            // 横クロップ位置は 2026-07-19 に廃止（横方向はキャラ基準位置で調整）。かわりに背景全体の縮尺
            scale: clamp(bg.scale, cfg.limits.backgroundScaleMin, cfg.limits.backgroundScaleMax, cfg.defaults.backgroundScale),
            positionY: clamp(bg.positionY, 0, 100, cfg.defaults.backgroundPositionY),
            stageCenterX: clamp(bg.stageCenterX, cfg.limits.stageCenterXMin, cfg.limits.stageCenterXMax, cfg.defaults.stageCenterX),
            ready: backgroundReady,
        },
        characters: casts,
        backgroundReady,
        charactersReady,
        artMode: backgroundReady && charactersReady,
    };
}

/** ゲーム側ファイル名（本家互換の start-menu-*.ext）。 */
function gameArtFileName(cfg, slot, projectFile) {
    const ext = path.extname(projectFile).replace(/^\./, '') || 'png';
    return slot === BACKGROUND_SLOT
        ? `${cfg.artFiles.backgroundBase}.${ext}`
        : `${cfg.artFiles.characterPrefix}${slot}.${ext}`;
}

/**
 * 現在の素材・調整値をリンク済みゲームへ反映する（ゲーム未リンクなら何もしない）。
 * 素材が欠けている間は artMode=false のフォールバック状態を書き込む（壊れた参照を出さない）。
 */
export function applyStartScreenToGame(projectId, gameIdArg = null) {
    const p = projectPaths(projectId);
    const state = readStartScreenState(projectId);
    const gameId = gameIdArg ?? readProjectMeta(projectId).gameId;
    if (!gameId) return { applied: false, reason: 'ゲーム未生成（② ベースデータ生成後に自動反映されます）', state };
    const cfg = startScreenConfig();
    const gameDir = requireGameDir(gameId);
    const images = {};
    const gameState = {
        artMode: state.artMode,
        panelSide: state.panelSide,
        background: null,
        characters: [],
    };
    if (state.artMode) {
        const bgGameFile = gameArtFileName(cfg, BACKGROUND_SLOT, state.background.file);
        images[bgGameFile] = readFileSync(path.join(p.startScreenImagesDir, state.background.file));
        gameState.background = {
            image: bgGameFile,
            scale: state.background.scale,
            positionY: state.background.positionY,
        };
        // 立ち絵の横方向基準: 台座中心（stageCenterX）とエンジン既定の立ち絵中心の差分を vw で加算する
        gameState.stageCenterX = state.background.stageCenterX;
        gameState.frameShiftVw = Math.round((state.background.stageCenterX - cfg.layout.engineFrameCenterX) * 10) / 10;
        gameState.characters = state.characters.map((c) => {
            const gameFile = gameArtFileName(cfg, c.castId, c.file);
            images[gameFile] = readFileSync(path.join(p.startScreenImagesDir, c.file));
            return {
                id: c.castId, image: gameFile,
                offsetX: c.offsetX, offsetY: c.offsetY, scale: c.scale, maskEnabled: c.maskEnabled,
            };
        });
    }
    const result = applyStartScreenToGameDir(gameDir, gameState, images);
    return { applied: true, gameId, artMode: state.artMode, result, state };
}

// ── プロンプト合成 ─────────────────────────────

function composeVars(analysis, design, panelSide) {
    const places = [
        ...asArray(design?.landmarks).map((l) => str(l?.nameJa)),
        ...asArray(design?.buildings).map((b) => str(b?.nameJa)),
        ...asArray(design?.areas).map((a) => str(a?.nameJa)),
    ].filter(Boolean).slice(0, 8);
    return {
        title: str(analysis?.game?.title?.ja) || str(analysis?.game?.title?.en) || '(untitled)',
        genre: str(analysis?.game?.genre) || 'Fantasy',
        worldSetting: str(analysis?.game?.worldSetting) || str(analysis?.summary?.maps) || '(no description)',
        worldTemplate: str(design?.foundation?.baseTemplateId) || 'bottom_world',
        skyMood: str(design?.environment?.skyMood) || '(unspecified)',
        lightingMood: str(design?.environment?.lightingMood) || '(unspecified)',
        places: places.join(' / ') || '(unspecified)',
        panelSideEn: panelSide === 'left' ? 'left' : 'right',
        characterSideEn: panelSide === 'left' ? 'right' : 'left',
    };
}

/** 背景の英語プロンプトを LLM で合成する（失敗時は決定的テンプレートへフォールバック）。 */
async function composeBackgroundPrompt(projectId, { onLog = () => {} } = {}) {
    const cfg = startScreenConfig();
    const analysis = requireAnalysis(projectId);
    const design = readWorldDesign(projectId);
    if (!design) throw new Error('ワールド設計（world-design.json）がありません。先に「④ ワールド設計」を実行してください。');
    const state = readStartScreenState(projectId);
    const vars = composeVars(analysis, design, state.panelSide);
    try {
        const r = await callLlmJson('imagePrompt', {
            system: cfg.composeSystem,
            prompt: fillTemplate(cfg.composeBackgroundPrompt, vars),
            onLog,
        });
        addUsage(projectId, r.usage, r.calls);
        const promptEn = str(r.json?.promptEn);
        if (promptEn) return promptEn;
        onLog('  ⚠ 背景プロンプトの合成応答が空のため、定型テンプレートで生成します');
    } catch (e) {
        onLog(`  ⚠ 背景プロンプトの合成に失敗（${String(e.message).slice(0, 120)}）。定型テンプレートで生成します`);
    }
    return fillTemplate(cfg.backgroundFallbackPrompt, vars);
}

/**
 * 背景専用の横長（16:9 目安）生成パラメータ。正方形背景は 16:9 画面の cover 表示で
 * 横方向の余白が出ず「横クロップ位置」が効かないため、対応モデルでは横長で生成する。
 * @returns {{ model: string, params: object } | null} 上書き設定が無いモデルは null
 */
export function backgroundImageCallSpec(cfg, baseSpec) {
    const wide = cfg.backgroundImageParams?.[baseSpec.model];
    if (!wide || typeof wide !== 'object') return null;
    const overrides = Object.fromEntries(Object.entries(wide).filter(([key]) => !key.startsWith('_')));
    if (Object.keys(overrides).length === 0) return null;
    return { model: baseSpec.model, params: { ...baseSpec.params, ...overrides } };
}

/** 横長パラメータがモデル側で拒否された場合だけ、標準パラメータへ落として再試行してよいか。
 *  キー・残高・規制・レート・タイムアウト起因は別問題のため再試行しない（二重課金・誤誘導防止）。 */
export function shouldFallbackToDefaultParams(error) {
    return ['openrouter_error', 'image_generation_failed'].includes(error?.code) && error?.retryable !== true;
}

/** ③-1 の promptEn（subjectEn + ポーズ定型文）から subjectEn 部分を取り出す。 */
export function subjectFromPromptEntry(entry, imagePromptsCfg = pipelineConfig().imagePrompts ?? {}) {
    const suffixes = [imagePromptsCfg.aPoseSuffixEn, imagePromptsCfg.scenePoseSuffixEn, imagePromptsCfg.naturalSuffixEn]
        .map((s) => str(s)).filter(Boolean);
    const promptEn = str(entry?.promptEn);
    for (const suffix of suffixes) {
        if (promptEn.endsWith(suffix)) {
            return promptEn.slice(0, -suffix.length).replace(/[.\s]+$/, '');
        }
    }
    return promptEn;
}

/** 立ち絵の英語プロンプト（外見は ③-1 subjectEn と同源＝キャラ生成の見た目に合わせる）。 */
export function buildCharacterArtPrompt(promptEntry) {
    const cfg = startScreenConfig();
    const subject = subjectFromPromptEntry(promptEntry);
    if (!subject) return null;
    return `${subject.replace(/[。.\s]+$/, '')}. ${cfg.characterSuffixEn}`;
}

// ── 生成・アップロード・設定 ─────────────────────

function requirePlayablePromptEntries(projectId, casts) {
    const idx = readJsonIfExists(projectPaths(projectId).imagePromptsIndex);
    const entries = new Map(asArray(idx?.prompts).map((e) => [e.castId, e]));
    const missing = casts.filter((c) => !str(entries.get(c.id)?.promptEn));
    if (missing.length > 0) {
        throw new Error(`プレイアブルの画像生成プロンプトがありません: ${missing.map((c) => c.id).join(', ')}（先に「③-1 画像生成プロンプト」を実行してください）`);
    }
    return entries;
}

/**
 * 開始画面素材の自動生成。
 * @param {object} opts { background?: true=背景を(再)生成 / null=未生成なら生成, castIds?: 対象キャスト（null=未生成のみ）,
 *                        model?: 画像モデル上書き, batch?: 並列化, onLog, signal }
 */
export async function runStartScreenGenerate(projectId, { background = null, castIds = null, model = null, batch = false, onLog = () => {}, signal = null } = {}) {
    requireProjectDir(projectId);
    const p = projectPaths(projectId);
    const cfg = startScreenConfig();
    const analysis = requireAnalysis(projectId);
    const state = readStartScreenState(projectId);
    const casts = playableStartScreenCasts(analysis);
    if (casts.length === 0) throw new Error('プレイアブルキャストが見つかりません（① 解析の結果を確認してください）。');

    const wantBackground = background === true
        || (background === null && castIds === null && !state.backgroundReady);
    const targetCasts = casts.filter((c) => (castIds
        ? castIds.includes(c.id)
        : background !== true && !state.characters.find((sc) => sc.castId === c.id)?.ready));
    if (!wantBackground && targetCasts.length === 0) {
        throw new Error('生成対象がありません（背景・立ち絵とも生成済みです。再生成する場合は対象を指定してください）。');
    }
    const promptEntries = targetCasts.length > 0 ? requirePlayablePromptEntries(projectId, targetCasts) : new Map();
    const spec = resolveImageCallSpec(llmConfig().image ?? {}, { model });
    onLog(`開始画面素材を生成します（${[wantBackground ? '背景' : null, targetCasts.length > 0 ? `立ち絵 ${targetCasts.length}体` : null].filter(Boolean).join(' + ')} / ${spec.provider} / ${spec.model}）`);

    // 背景プロンプトの決定（手動編集済みならそれを尊重。LLM 合成は画像生成前に 1 回だけ）
    let backgroundPrompt = null;
    if (wantBackground) {
        const stored = readSettings(p).background ?? {};
        if (stored.promptEdited === true && str(stored.promptEn)) {
            backgroundPrompt = str(stored.promptEn);
            onLog('  背景プロンプト: 手動編集済みの内容を使用します');
        } else {
            onLog('  背景プロンプトを ④ ワールド設計から合成しています…');
            backgroundPrompt = await composeBackgroundPrompt(projectId, { onLog });
        }
        signal?.throwIfAborted();
    }

    const targets = [
        ...(wantBackground ? [{ slot: BACKGROUND_SLOT, label: '背景' }] : []),
        ...targetCasts.map((c) => ({ slot: c.id, label: `${c.name?.ja ?? c.id}（${c.id}）立ち絵`, cast: c })),
    ];
    const generated = [];
    const failures = [];
    const castLogs = {};
    const targetOrder = new Map(targets.map((t, i) => [t.slot, i]));
    const concurrency = batchCharacterConcurrency('imageGeneration', batch);
    let stopScheduling = false;
    await mapWithConcurrency(targets, async (target) => {
        const lines = [];
        castLogs[target.slot] = lines;
        const log = (line) => { lines.push(String(line)); onLog(line); };
        try {
            log(`  ── ${target.label}を生成中…`);
            const isBackground = target.slot === BACKGROUND_SLOT;
            const prompt = isBackground
                ? `${backgroundPrompt.replace(/[。.\s]+$/, '')}. ${cfg.backgroundSuffixEn}`
                : buildCharacterArtPrompt(promptEntries.get(target.slot));
            if (!prompt) throw new Error('プロンプトを組み立てられません（③-1 の promptEn を確認してください）');
            // 背景は横長（16:9 目安）で生成する（横クロップ位置を有効にするため）。
            // モデルが横長パラメータを拒否した場合だけ標準サイズで再試行する
            const wide = isBackground ? backgroundImageCallSpec(cfg, spec) : null;
            let r;
            if (wide) {
                try {
                    log(`    背景は横長（16:9 目安）で生成します（${Object.entries(wide.params).map(([k, v]) => `${k}=${v}`).join(', ')}）`);
                    r = await callImage({ prompt, model: wide.model, params: wide.params, onLog: log });
                } catch (e) {
                    if (!shouldFallbackToDefaultParams(e)) throw e;
                    log(`    ⚠ 横長パラメータがこのモデルで拒否されました（${String(e.message).slice(0, 100)}）。標準サイズで再試行します（横クロップ位置は縦長画面のみ有効になります）`);
                    r = await callImage({ prompt, model, onLog: log });
                }
            } else {
                r = await callImage({ prompt, model, onLog: log });
            }
            let bytes = Buffer.from(r.imageBase64, 'base64');
            let contentType = r.contentType;
            let hasAlpha = false;
            if (!isBackground) {
                // 立ち絵は透過 RGBA を優先（本家 §2.2 / §15.3。背景は環境画のため透過処理しない）
                const post = llmConfig().image?.postProcess ?? {};
                try {
                    const tolerance = Number(post.backgroundTolerancePerModel?.[r.model] ?? post.backgroundTolerance) || 32;
                    const t = await toTransparentPng(bytes, contentType, { tolerance });
                    bytes = t.buffer;
                    contentType = t.contentType;
                    hasAlpha = t.mode === 'native' || t.mode === 'removed';
                    if (t.mode === 'native') log('    🪄 透過PNG: モデル出力が元から透明背景（そのまま保存）');
                    else if (t.mode === 'removed') log(`    🪄 透過PNG: 白背景を透過化（画素の ${Math.round((t.removedRatio ?? 0) * 100)}% を除去）`);
                    else log(`    ⚠ 透過PNG: 非対応形式（${contentType}）のため原本のまま保存します（楕円マスクで馴染ませます）`);
                    // 脚の間に閉じ込められた白ポケット・足元の床影は外周フラッドフィルでは消えないため追加補正する
                    if (hasAlpha && cfg.portraitFootClear.enabled) {
                        const f = await clearFootArtifacts(bytes, contentType, { tolerance, ...cfg.portraitFootClear });
                        if (f.mode === 'refined' && (f.removedRatio ?? 0) > 0) {
                            bytes = f.buffer;
                            contentType = f.contentType;
                            log(`    🦶 足元補正: 脚の間の白残り・床影を追加透過（画素の ${(f.removedRatio * 100).toFixed(1)}%）`);
                        }
                    }
                } catch (e) {
                    log(`    ⚠ 透過PNG化に失敗（${String(e.message).slice(0, 120)}）。原本のまま保存します（楕円マスクで馴染ませます）`);
                }
            }
            const ext = IMAGE_EXT_BY_TYPE[contentType] ?? 'png';
            const fileName = writeSlotImage(p, isBackground ? 'background' : target.slot, bytes, ext);
            const settings = readSettings(p);
            const patch = {
                file: fileName,
                source: 'auto',
                model: r.model,
                generatedAt: new Date().toISOString(),
                promptEn: isBackground ? backgroundPrompt : prompt,
            };
            if (isBackground) {
                settings.background = { ...(settings.background ?? {}), ...patch, promptEdited: settings.background?.promptEdited === true };
            } else {
                settings.characters = settings.characters ?? {};
                settings.characters[target.slot] = {
                    ...(settings.characters[target.slot] ?? {}),
                    ...patch,
                    // 透過 RGBA になった立ち絵は楕円マスク不要（本家 §9.3）。RGB のままなら ON（UI で調整可能）
                    maskEnabled: !hasAlpha,
                };
            }
            writeSettings(p, settings);
            generated.push({
                slot: target.slot, file: fileName, bytes: bytes.length, elapsedMs: r.elapsedMs,
                ...(Number.isFinite(r.usageUsd) ? { costUsd: r.usageUsd } : {}),
            });
            log(`    ✔ ${fileName}（${Math.round(bytes.length / 1024)}KB・${Math.round((r.elapsedMs ?? 0) / 1000)}秒）`);
        } catch (e) {
            failures.push({ slot: target.slot, code: e.code ?? 'error', message: e.message });
            log(`    ✖ ${target.label}: ${e.message}`);
            if (['token_missing', 'openrouter_auth', 'openrouter_credits'].includes(e.code)) {
                log('  API キーまたは残高の問題のため中断します（解消後に再実行できます）');
                stopScheduling = true;
            }
        }
    }, { concurrency, signal, shouldStart: () => !stopScheduling });
    generated.sort((a, b) => targetOrder.get(a.slot) - targetOrder.get(b.slot));
    failures.sort((a, b) => targetOrder.get(a.slot) - targetOrder.get(b.slot));

    // 生成できた分までをゲームへ反映（全素材がそろっていなければ安全なフォールバック状態を書く）
    let applied = null;
    try {
        applied = applyStartScreenToGame(projectId);
        if (applied.applied) {
            onLog(applied.artMode
                ? `✔ ゲームへ反映しました（開始画面アート有効 / ${applied.gameId}）`
                : `ℹ 素材がそろうまでゲームは従来の開始画面のままです（${applied.gameId}）`);
        }
    } catch (e) {
        failures.push({ slot: 'apply', code: 'apply_failed', message: e.message });
        onLog(`✖ ゲームへの反映に失敗: ${e.message}`);
    }
    const finalState = readStartScreenState(projectId);
    recordStep(projectId, 'startScreen', {
        ok: failures.length === 0 && finalState.artMode,
        background: finalState.backgroundReady,
        characters: `${finalState.characters.filter((c) => c.ready).length}/${finalState.characters.length}`,
    });
    auditLog('story-start-screen', { projectId, generated: generated.length, failed: failures.length, artMode: finalState.artMode });
    onLog(`✔ 開始画面素材の生成が完了: 成功 ${generated.length} / 失敗 ${failures.length}`);
    return { ok: failures.length === 0, generated, failures, castLogs, state: finalState };
}

/**
 * 自作画像への差し替え（手動アップロード）。slot は 'background' またはプレイアブル castId。
 * 画像生成 API が使えない環境でも、この経路だけで開始画面を完成させられる（本家 §6.5）。
 */
export function saveStartScreenUpload(projectId, slot, { dataBase64, fileName = null } = {}) {
    requireProjectDir(projectId);
    const p = projectPaths(projectId);
    const analysis = requireAnalysis(projectId);
    const casts = playableStartScreenCasts(analysis);
    const isBackground = slot === BACKGROUND_SLOT;
    if (!isBackground && !casts.some((c) => c.id === slot)) {
        throw new Error(`アップロード先が不明です: ${slot}（'background' またはプレイアブル castId を指定してください）`);
    }
    if (typeof dataBase64 !== 'string' || dataBase64.trim() === '') throw new Error('画像データ（base64）が空です');
    const bytes = Buffer.from(dataBase64.replace(/^data:[^;]+;base64,/, ''), 'base64');
    if (bytes.length === 0) throw new Error('画像データを base64 として解釈できません');
    if (bytes.length > MAX_UPLOAD_BYTES) {
        throw new Error(`画像が大きすぎます（${Math.round(bytes.length / 1024 / 1024)}MB。上限 ${MAX_UPLOAD_BYTES / 1024 / 1024}MB）`);
    }
    const type = sniffImageType(bytes);
    if (!type) throw new Error('対応していない画像形式です（png / jpeg / webp のみ）');
    const file = writeSlotImage(p, isBackground ? 'background' : slot, bytes, IMAGE_EXT_BY_TYPE[type]);
    const settings = readSettings(p);
    const patch = {
        file,
        source: 'upload',
        model: null,
        generatedAt: new Date().toISOString(),
        ...(fileName ? { originalName: String(fileName).slice(0, 120) } : {}),
    };
    if (isBackground) settings.background = { ...(settings.background ?? {}), ...patch };
    else {
        settings.characters = settings.characters ?? {};
        settings.characters[slot] = { ...(settings.characters[slot] ?? {}), ...patch };
    }
    writeSettings(p, settings);
    const applied = applyStartScreenToGame(projectId);
    const state = applied.state ?? readStartScreenState(projectId);
    recordStep(projectId, 'startScreen', {
        ok: state.artMode,
        background: state.backgroundReady,
        characters: `${state.characters.filter((c) => c.ready).length}/${state.characters.length}`,
    });
    auditLog('story-start-screen-upload', { projectId, slot, bytes: bytes.length });
    return { ok: true, slot, file, bytes: bytes.length, artMode: state.artMode, state };
}

/**
 * 配置・見た目の調整値を保存してゲームへ再適用する。
 * patch = { panelSide?, background?: { positionX?, positionY?, promptEn? },
 *           characters?: { <castId>: { offsetX?, offsetY?, scale?, maskEnabled? } } }
 */
export function saveStartScreenSettings(projectId, patch = {}) {
    requireProjectDir(projectId);
    const p = projectPaths(projectId);
    const cfg = startScreenConfig();
    const settings = readSettings(p);
    if (patch.panelSide === 'left' || patch.panelSide === 'right') settings.panelSide = patch.panelSide;
    if (patch.background && typeof patch.background === 'object') {
        const bg = settings.background ?? (settings.background = {});
        if ('scale' in patch.background) bg.scale = clamp(patch.background.scale, cfg.limits.backgroundScaleMin, cfg.limits.backgroundScaleMax, cfg.defaults.backgroundScale);
        if ('positionY' in patch.background) bg.positionY = clamp(patch.background.positionY, 0, 100, cfg.defaults.backgroundPositionY);
        if ('stageCenterX' in patch.background) {
            bg.stageCenterX = clamp(patch.background.stageCenterX, cfg.limits.stageCenterXMin, cfg.limits.stageCenterXMax, cfg.defaults.stageCenterX);
        }
        if ('promptEn' in patch.background) {
            const promptEn = str(patch.background.promptEn);
            bg.promptEn = promptEn || null;
            bg.promptEdited = promptEn !== '';
        }
    }
    if (patch.characters && typeof patch.characters === 'object') {
        settings.characters = settings.characters ?? {};
        for (const [castId, c] of Object.entries(patch.characters)) {
            if (!c || typeof c !== 'object') continue;
            const entry = settings.characters[castId] ?? (settings.characters[castId] = {});
            if ('offsetX' in c) entry.offsetX = clamp(c.offsetX, -cfg.limits.offsetAbsMax, cfg.limits.offsetAbsMax, cfg.defaults.offsetX);
            if ('offsetY' in c) entry.offsetY = clamp(c.offsetY, -cfg.limits.offsetAbsMax, cfg.limits.offsetAbsMax, cfg.defaults.offsetY);
            if ('scale' in c) entry.scale = clamp(c.scale, cfg.limits.scaleMin, cfg.limits.scaleMax, cfg.defaults.scale);
            if ('maskEnabled' in c) entry.maskEnabled = c.maskEnabled === true;
        }
    }
    writeSettings(p, settings);
    const applied = applyStartScreenToGame(projectId);
    auditLog('story-start-screen-settings', { projectId, applied: applied.applied });
    return { ok: true, applied: applied.applied, state: applied.state };
}

/**
 * 保存済み立ち絵の足元補正（無償・API 再課金なし）。
 * 脚の間に閉じ込められた白ポケットと足元の床影を追加透過し、ゲームへ再適用する。
 * 不透明画像（RGB アップロード）は背景の透過化も同時に行う。
 */
export async function refineStartScreenPortrait(projectId, castId) {
    requireProjectDir(projectId);
    const p = projectPaths(projectId);
    const cfg = startScreenConfig();
    const state = readStartScreenState(projectId);
    const entry = state.characters.find((c) => c.castId === castId);
    if (!entry) throw new Error(`プレイアブルが見つかりません: ${castId}`);
    if (!entry.ready || !entry.file) throw new Error(`「${castId}」の立ち絵がまだありません（先に生成またはアップロードしてください）。`);
    const ext = path.extname(entry.file).replace(/^\./, '').toLowerCase();
    const contentType = ext === 'png' ? 'image/png' : ext === 'webp' ? 'image/webp' : 'image/jpeg';
    const bytes = readFileSync(path.join(p.startScreenImagesDir, entry.file));
    const post = llmConfig().image?.postProcess ?? {};
    const tolerance = Number(post.backgroundTolerance) || 32;
    const result = await clearFootArtifacts(bytes, contentType, { tolerance, ...cfg.portraitFootClear });
    if (result.mode === 'unsupported') {
        throw new Error('この画像形式は補正に対応していません（png / jpeg の画像で実行してください）。');
    }
    const changed = (result.removedRatio ?? 0) > 0 || result.backgroundRemoved;
    if (changed) {
        const file = writeSlotImage(p, castId, result.buffer, 'png');
        const settings = readSettings(p);
        settings.characters = settings.characters ?? {};
        settings.characters[castId] = {
            ...(settings.characters[castId] ?? {}),
            file,
            refinedAt: new Date().toISOString(),
            // 透過 RGBA になったため楕円マスクは不要（本家 §9.3。必要なら UI で再度 ON にできる）
            maskEnabled: false,
        };
        writeSettings(p, settings);
        applyStartScreenToGame(projectId);
    }
    auditLog('story-start-screen-refine', { projectId, castId, removedRatio: result.removedRatio ?? 0, backgroundRemoved: result.backgroundRemoved });
    return {
        ok: true,
        castId,
        changed,
        removedRatio: result.removedRatio ?? 0,
        backgroundRemoved: result.backgroundRemoved,
        state: readStartScreenState(projectId),
    };
}

/** UI 表示用: 背景プロンプトのプレビュー（LLM 合成前の変数展開済みフォールバック文）。 */
export function previewBackgroundPromptVars(projectId) {
    const analysis = requireAnalysis(projectId);
    const design = readWorldDesign(projectId);
    const state = readStartScreenState(projectId);
    if (!design) return null;
    return composeVars(analysis, design, state.panelSide);
}
